// src/screens/PlaybookEditor.tsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { v4 as uuid } from "uuid";
import { Save, Plus, Trash2, GripVertical, Search } from "lucide-react";
import i18n from "../i18n";

/** ============ Types ============ **/
type Mode = "create" | "edit";
type Play = { id: string; name: string; tag_ids?: number[] };

type BlockItem = {
  id: string;
  play_id: string;
  order_idx: number;
  notes?: string;
  play?: Play; // optionnel au chargement
};

type Block = {
  id: string;
  title: string;
  order_idx: number;
  items: BlockItem[];
};

type TeamRow = { id: string; team_name: string };

/* ===== i18n mini-hook ===== */
function useI18nSafe() {
  const [lng, setLng] = useState(
    (i18n.resolvedLanguage || i18n.language || "fr").toLowerCase()
  );
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = (key: string, options?: Record<string, unknown>): string =>
    options != null ? String((i18n.t as any)(key, options)) : String((i18n.t as any)(key));
  return { t, lng };
}

/** ============ Helpers DnD ============ **/
const pid = (playId: string) => `play:${playId}`;                 // source: colonne gauche
const bid = (blockId: string) => `block:${blockId}`;              // drop zone bloc
const iid = (blockId: string, itemId: string) => `item:${blockId}:${itemId}`; // élément d’un bloc

function parseId(raw: string): { type: "play" | "block" | "item"; a?: string; b?: string } {
  const p = String(raw).split(":");
  if (p[0] === "play") return { type: "play", a: p[1] };
  if (p[0] === "block") return { type: "block", a: p[1] };
  if (p[0] === "item")  return { type: "item",  a: p[1], b: p[2] };
  return { type: "block" };
}

/** ============ Component ============ **/
export const PlaybookEditor = ({ mode }: { mode: Mode }) => {
  const { t } = useI18nSafe();
  const params = useParams();
  const nav = useNavigate();

  const [loading, setLoading] = useState(true);

  // meta
  const [teams, setTeams] = useState<TeamRow[]>([]);
  const [teamId, setTeamId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  // plays source
  const [allPlays, setAllPlays] = useState<Play[]>([]);
  const [query, setQuery] = useState("");

  // blocs
  const [blocks, setBlocks] = useState<Block[]>([
    { id: uuid(), title: t("playbookeditor:defaults.block_title", { index: 1 }), order_idx: 0, items: [] },
  ]);

  /** --------- Load --------- **/
  useEffect(() => {
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) { setLoading(false); return; }

        // équipes de l'user
        const { data: teamRows } = await supabase
          .from("teams")
          .select("id, team_name")
          .eq("user_id", user.id)
          .order("team_name", { ascending: true });

        setTeams(teamRows || []);
        if (!teamId && teamRows?.length) setTeamId(teamRows[0].id);

        // plays
        const { data: playsRows } = await supabase
          .from("plays")
          .select("id, name, tag_ids")
          .order("name", { ascending: true });

        setAllPlays(playsRows || []);

        // edit mode
        if (mode === "edit" && params.id) {
          const { data: pb } = await supabase
            .from("playbooks")
            .select("id, name, description, team_id")
            .eq("id", params.id)
            .maybeSingle();

          if (pb) {
            setName(pb.name);
            setDescription(pb.description || "");
            setTeamId(pb.team_id);

            const { data: pbb } = await supabase
              .from("playbook_blocks")
              .select("id, title, order_idx")
              .eq("playbook_id", pb.id)
              .order("order_idx", { ascending: true });

            const blocksRows = pbb || [];
            const withItems = await Promise.all(
              blocksRows.map(async (b) => {
                const { data: items } = await supabase
                  .from("playbook_items")
                  .select("id, play_id, order_idx, notes")
                  .eq("block_id", b.id)
                  .order("order_idx", { ascending: true });

                const mapped: BlockItem[] = (items || []).map((it) => ({
                  ...it,
                  play: (playsRows || []).find((p) => p.id === it.play_id),
                }));
                return { id: b.id, title: b.title, order_idx: b.order_idx, items: mapped };
              })
            );

            setBlocks(withItems);
          }
        }
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, params.id]);

  /** --------- Filtre plays --------- **/
  const filteredPlays = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allPlays;
    return allPlays.filter((p) => p.name.toLowerCase().includes(q));
  }, [allPlays, query]);

  /** --------- HTML5 Drag helpers --------- **/
  const onDragStart = (ev: React.DragEvent, dragId: string) => {
    ev.dataTransfer.setData("text/plain", dragId);
  };
  const allowDrop = (ev: React.DragEvent) => ev.preventDefault();

  // Drop sur un bloc "vide" (ou en bas du bloc)
  const onDropOnBlock = (ev: React.DragEvent, blockId: string) => {
    ev.preventDefault();
    const raw = ev.dataTransfer.getData("text/plain");
    const src = parseId(raw);

    // Ajouter depuis la colonne plays
    if (src.type === "play" && src.a) {
      setBlocks((prev) =>
        prev.map((b) => {
          if (b.id !== blockId) return b;
          const next = [...b.items];
          next.push({ id: uuid(), play_id: src.a!, order_idx: next.length });
          return { ...b, items: next };
        })
      );
      return;
    }

    // Déplacer un item depuis un autre bloc → fin du bloc cible
    if (src.type === "item" && src.a && src.b) {
      const fromBlockId = src.a;
      const itemId = src.b;
      if (fromBlockId === blockId) return; // même bloc → on ne bouge pas ici

      setBlocks((prev) => {
        const copy = structuredClone(prev) as Block[];
        const s = copy.find((x) => x.id === fromBlockId);
        const d = copy.find((x) => x.id === blockId);
        if (!s || !d) return prev;

        const idx = s.items.findIndex((i) => i.id === itemId);
        if (idx === -1) return prev;

        const [moved] = s.items.splice(idx, 1);
        s.items = s.items.map((it, i) => ({ ...it, order_idx: i }));
        d.items.push({ ...moved, order_idx: d.items.length });
        return copy;
      });
    }
  };

  // Drop "sur" un ITEM précis → insertion avant cet item
  const onDropOnItem = (ev: React.DragEvent, targetBlockId: string, targetItemId: string) => {
    ev.preventDefault();
    const raw = ev.dataTransfer.getData("text/plain");
    const src = parseId(raw);

    setBlocks((prev) => {
      const copy = structuredClone(prev) as Block[];
      const block = copy.find((b) => b.id === targetBlockId);
      if (!block) return prev;

      const targetIndex = block.items.findIndex((i) => i.id === targetItemId);
      if (targetIndex === -1) return prev;

      // 1) Depuis la colonne plays → on insère un nouvel item avant targetIndex
      if (src.type === "play" && src.a) {
        const newItem: BlockItem = { id: uuid(), play_id: src.a, order_idx: targetIndex };
        block.items.splice(targetIndex, 0, newItem);
        block.items = block.items.map((it, i) => ({ ...it, order_idx: i }));
        return copy;
      }

      // 2) Déplacement d’un item (même bloc ou inter-blocs)
      if (src.type === "item" && src.a && src.b) {
        const fromBlockId = src.a;
        const itemId = src.b;

        const fromBlock = copy.find((b) => b.id === fromBlockId);
        if (!fromBlock) return prev;
        const fromIdx = fromBlock.items.findIndex((i) => i.id === itemId);
        if (fromIdx === -1) return prev;

        const [moved] = fromBlock.items.splice(fromIdx, 1);

        const sameBlock = fromBlockId === targetBlockId;
        const insertAt = sameBlock && fromIdx < targetIndex ? targetIndex - 1 : targetIndex;

        block.items.splice(insertAt, 0, { ...moved });
        fromBlock.items = fromBlock.items.map((it, i) => ({ ...it, order_idx: i }));
        block.items = block.items.map((it, i) => ({ ...it, order_idx: i }));

        return copy;
      }

      return prev;
    });
  };

  /** --------- Actions UI --------- **/
  const addBlock = () => {
    setBlocks((prev) => [
      ...prev,
      { id: uuid(), title: t("playbookeditor:defaults.block_title", { index: prev.length + 1 }), order_idx: prev.length, items: [] },
    ]);
  };

  const removeBlock = (blockId: string) => {
    setBlocks((prev) =>
      prev.filter((b) => b.id !== blockId).map((b, i) => ({ ...b, order_idx: i }))
    );
  };

  const removeItem = (blockId: string, itemId: string) => {
    setBlocks((prev) =>
      prev.map((b) => {
        if (b.id !== blockId) return b;
        const next = b.items.filter((i) => i.id !== itemId).map((it, idx) => ({ ...it, order_idx: idx }));
        return { ...b, items: next };
      })
    );
  };

  /** --------- Save --------- **/
  const saveAll = async () => {
    if (!teamId || !name.trim()) {
      alert(t("playbookeditor:alerts.select_team_and_name"));
      return;
    }
    setLoading(true);
    try {
      let playbookId = params.id as string | undefined;

      if (mode === "create") {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error("Not authenticated");
        const { data, error } = await supabase
          .from("playbooks")
          .insert({
            id: uuid(),
            user_id: user.id,
            team_id: teamId,
            name,
            description,
          })
          .select("id")
          .single();
        if (error) throw error;
        playbookId = data.id;
      } else {
        const { error } = await supabase
          .from("playbooks")
          .update({ name, description, team_id: teamId })
          .eq("id", playbookId);
        if (error) throw error;

        // on remplace blocs/items pour simplifier
        const { data: existingBlocks } = await supabase
          .from("playbook_blocks")
          .select("id")
          .eq("playbook_id", playbookId);
        const blockIds = (existingBlocks || []).map((r) => r.id);
        if (blockIds.length) {
          await supabase.from("playbook_items").delete().in("block_id", blockIds);
          await supabase.from("playbook_blocks").delete().eq("playbook_id", playbookId);
        }
      }

      // blocs
      const blocksPayload = blocks.map((b) => ({
        id: b.id,
        playbook_id: playbookId,
        title: b.title,
        order_idx: b.order_idx,
      }));
      if (blocksPayload.length) {
        const { error: e1 } = await supabase.from("playbook_blocks").insert(blocksPayload);
        if (e1) throw e1;
      }

      // items
      const itemsPayload = blocks.flatMap((b) =>
        b.items.map((it) => ({
          id: it.id,
          block_id: b.id,
          play_id: it.play_id,
          order_idx: it.order_idx,
          notes: it.notes ?? null,
        }))
      );
      if (itemsPayload.length) {
        const { error: e2 } = await supabase.from("playbook_items").insert(itemsPayload);
        if (e2) throw e2;
      }

      nav("/playbooks");
    } catch (err) {
      console.error(err);
      alert(t("playbookeditor:alerts.save_error"));
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="p-6">{t("common:loading")}</div>;

  return (
    <div className="max-w-7xl mx-auto p-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-4">
        <div className="flex-1">
          <div className="text-sm opacity-70">
            {mode === "create" ? t("playbookeditor:header.new") : t("playbookeditor:header.edit")}
          </div>
          <input
            className="mt-1 w-full rounded-xl border px-3 py-2 dark:bg-zinc-950 dark:border-zinc-800"
            placeholder={t("playbookeditor:placeholders.name")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-label={t("playbookeditor:labels.name")}
          />
          <textarea
            className="mt-2 w-full rounded-xl border px-3 py-2 dark:bg-zinc-950 dark:border-zinc-800"
            placeholder={t("playbookeditor:placeholders.description")}
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            aria-label={t("playbookeditor:labels.description")}
          />
        </div>
        <div className="flex items-center gap-2">
          {/* Sélecteur d'équipe */}
          <select
            className="rounded-xl border px-3 py-2 dark:bg-zinc-950 dark:border-zinc-800"
            value={teamId ?? ""}
            onChange={(e) => setTeamId(e.target.value || null)}
            aria-label={t("playbookeditor:labels.team")}
          >
            <option value="" disabled>{t("playbookeditor:placeholders.team")}</option>
            {teams.map((t) => (
              <option key={t.id} value={t.id}>{t.team_name}</option>
            ))}
          </select>

          <button
            onClick={saveAll}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-black text-white dark:bg-white dark:text-black"
            title={t("playbookeditor:actions.save_title")}
          >
            <Save size={18} /> {t("playbookeditor:actions.save")}
          </button>
        </div>
      </div>

      {/* Corps */}
      <div className="grid lg:grid-cols-3 gap-4">
        {/* Colonne gauche : plays */}
        <div className="lg:col-span-1 rounded-2xl border p-3 dark:border-zinc-800">
          <div className="flex items-center gap-2 mb-2">
            <Search size={16} className="opacity-70" />
            <input
              className="w-full rounded-xl border px-3 py-2 dark:bg-zinc-950 dark:border-zinc-800"
              placeholder={t("playbookeditor:placeholders.search")}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label={t("playbookeditor:labels.search")}
            />
          </div>
          <div className="text-xs opacity-60 mb-2">
            {t("playbookeditor:helpers.drag_to_block")}
          </div>
          <ul className="space-y-1">
            {filteredPlays.map((p) => (
              <li
                key={p.id}
                draggable
                onDragStart={(ev) => onDragStart(ev, pid(p.id))}
                className="flex items-center justify-between rounded-xl border px-3 py-2 dark:border-zinc-800 cursor-grab bg-white dark:bg-zinc-950"
                title={t("playbookeditor:titles.drag")}
              >
                <span className="truncate">{p.name}</span>
                <GripVertical className="opacity-50" size={16} aria-hidden />
              </li>
            ))}
          </ul>
        </div>

        {/* Zone blocs */}
        <div className="lg:col-span-2 space-y-4">
          <button
            onClick={addBlock}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border dark:border-zinc-800"
            title={t("playbookeditor:actions.add_block_title")}
          >
            <Plus size={18} /> {t("playbookeditor:actions.add_block")}
          </button>

          {blocks
            .sort((a, b) => a.order_idx - b.order_idx)
            .map((b) => (
              <div key={b.id} className="rounded-2xl border dark:border-zinc-800 p-3">
                <div className="flex items-center justify-between mb-2">
                  <input
                    className="font-medium text-lg bg-transparent outline-none"
                    value={b.title}
                    onChange={(e) =>
                      setBlocks((prev) => prev.map((x) => (x.id === b.id ? { ...x, title: e.target.value } : x)))
                    }
                    aria-label={t("playbookeditor:labels.block_title")}
                  />
                  <button
                    onClick={() => removeBlock(b.id)}
                    className="p-2 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-900"
                    title={t("playbookeditor:actions.delete_block_title")}
                    aria-label={t("playbookeditor:actions.delete_block_title")}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>

                {/* Drop zone du bloc (fond) */}
                <div
                  id={bid(b.id)}
                  className="min-h-14 rounded-xl border border-dashed dark:border-zinc-800 p-2"
                  onDragOver={allowDrop}
                  onDrop={(ev) => onDropOnBlock(ev, b.id)}
                  aria-label={t("playbookeditor:labels.block_dropzone")}
                >
                  <ul className="space-y-2">
                    {b.items
                      .sort((a, c) => a.order_idx - c.order_idx)
                      .map((it) => (
                        <li
                          key={it.id}
                          id={iid(b.id, it.id)}
                          draggable
                          onDragStart={(ev) => onDragStart(ev, iid(b.id, it.id))}
                          onDragOver={allowDrop}
                          onDrop={(ev) => onDropOnItem(ev, b.id, it.id)}
                          className="flex items-center justify-between rounded-xl border px-3 py-2 dark:border-zinc-800 bg-white dark:bg-zinc-950"
                          title={t("playbookeditor:titles.drag_reorder")}
                        >
                          <div className="flex-1">
                            <div className="font-medium">
                              {allPlays.find((p) => p.id === it.play_id)?.name ?? t("playbookeditor:fallback.play")}
                            </div>
                            <input
                              className="w-full mt-1 text-sm rounded-lg border px-2 py-1 dark:border-zinc-800"
                              placeholder={t("playbookeditor:placeholders.notes")}
                              value={it.notes ?? ""}
                              onChange={(e) =>
                                setBlocks((prev) =>
                                  prev.map((bl) => {
                                    if (bl.id !== b.id) return bl;
                                    return {
                                      ...bl,
                                      items: bl.items.map((x) => (x.id === it.id ? { ...x, notes: e.target.value } : x)),
                                    };
                                  })
                                )
                              }
                              aria-label={t("playbookeditor:labels.item_notes")}
                            />
                          </div>
                          <div className="flex items-center gap-2 pl-2">
                            <GripVertical className="opacity-50" size={16} aria-hidden />
                            <button
                              onClick={() => removeItem(b.id, it.id)}
                              className="p-2 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-900"
                              title={t("playbookeditor:actions.remove_item_title")}
                              aria-label={t("playbookeditor:actions.remove_item_title")}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </li>
                      ))}
                  </ul>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};
